/* eslint no-param-reassign:  ["error", { "props": false }] */
/* eslint no-shadow: 0 */

import { get, debounce } from 'lodash';
import {
  getUserTaskList,
  getTaskEditDetails,
  getTaskDetails,
  getTaskEditDefaults,
  resortUserTaskList,
  removeMedia,
  addMediaUrl,
  editTask,
  reorderTaskMedia,
  addTaskToUser,
  removeTaskFromUser,
} from '@/api';

import {
  BROWSE_TASKS,
  SET_ALLOWED_CHANNEL_ACCESS,
  REORDER_FOLLOWING_TASKS,
  REORDER_REGISTERED_TASKS,
  FOLLOWING_TASKS,
  REGISTERED_TASKS,
  UNFOLLOW_TASK,
  SET_ERROR,
  EDIT_TASK,
  EDIT_TASK_SKILLS,
  GET_TASK_EDIT_DETAILS,
  TASK_EDIT_DEFAULTS,
  TASKS_OBJECT_ADD,
  TASKS_OBJECT_REMOVE,
  NEW_TASK_DATES,
  SET_TASK_DATE,
  REORDER_TASK_MEDIA,
  SHOW_HINT_TOGGLE,
  SET_TASK_LOCATION,
  ADD_MEDIA_URL,
  RESET_TASK_STATE,
  SET_PROJECT_ID,
  UPDATE_TASK_SCOPE,
} from '@/store/mutation-types';

import { saveState, loadState } from '@/utils/storage';

const defaultState = () => ({
  task_data: {
    task_name: '',
    project_id: null,
    // possible values are single, multi, unique
    task_status: '0',
    task_virtual: '0',
    task_allow_anon: '0',
    completion_type: 'multi',
    completion_limit: null,
    task_description: '',
    task_organizer_description: '',
    task_registered_description: '',
    task_location_restricted: false,
  },
  tasks_locations: [],
  tasks_data: [],
  tasks_skills: [],
  tasks_dates: [],
  tasks_organizers: [],
});

const state = {
  // defaults for tasks
  task_edit_defaults: {},
  taskEditLoading: false,
  // task create/edit details
  task_data: {
    task_name: '',
    project_id: null,
    // possible values are single, multi, unique
    task_status: '0',
    task_virtual: '0',
    task_allow_anon: '0',
    completion_type: 'multi',
    completion_limit: null,
    task_description: '',
    task_organizer_description: '',
    task_registered_description: '',
    task_location_restricted: false,
  },
  tasks_locations: [],
  // tasks data/media objects
  tasks_data: [],
  // task skills array
  tasks_skills: [],
  // task dates array
  tasks_dates: [],
  // list of all the organizers for  a task
  tasks_organizers: [],
  // list of all the tasks, used for browse
  tasks: [],
  // list of tasks belonging to the user
  followingTasksLoading: false,
  followingTasks: [],
  followingTasksRankRange: {},
  followingTasksTake: 20,
  followingTasksSkip: 0,
  followingTasksExhausted: false,

  registeredTasksLoading: false,
  registeredTasks: [],
  registeredTasksRankRange: {},
  registeredTasksTake: 20,
  registeredTasksSkip: 0,
  registeredTasksExhausted: false,
  // any error message that occurs during the process
  allowed_channel_access: [],
  error: {},
  showHint: loadState('showHint') === undefined ? true : loadState('showHint'),
};

const getters = {
  task(state) {
    return state.task_data;
  },
  taskEditLoading(state) {
    return state.taskEditLoading;
  },
  project(state) {
    return get(state, 'task_data.project[0].project_data', {});
  },
  skills(state) {
    return state.tasks_skills;
  },
  organizers(state) {
    return state.tasks_organizers;
  },
  tasks(state) {
    return state.tasks;
  },
  followingTasks(state) {
    return state.followingTasks;
  },
  registeredTasks(state) {
    return state.registeredTasks;
  },
  showHint(state) {
    return state.showHint;
  },
  timeLimited(state) {
    return state.tasks_dates.length > 0;
  },
  tasksDates(state) {
    return state.tasks_dates;
  },
  taskDate(state) {
    // NOTE: using first item currently as we support only one date
    return get(state.tasks_dates, '[0].task_date');
  },
  taskDuration(state) {
    // NOTE: using first item currently as we support only one date
    return get(state.tasks_dates, '[0].task_duration');
  },
  taskObjects(state) {
    return state.tasks_data;
  },
  followingTasksRankRange(state) {
    return state.followingTasksRankRange;
  },
  registeredTasksRankRange(state) {
    return state.registeredTasksRankRange;
  },
  taskAddress(state) {
    return get(state.tasks_locations, '[0].location_address');
  },
  taskRadius(state) {
    return get(state.tasks_locations, '[0].task_radius');
  },
  taskStatus(state) {
    return state.task_data.task_status;
  },
  allowedChannelAccess(state) {
    return state.task_data.channel_types_access || [];
  },
  userTaskScope(state) {
    return state.task_data.user_task_scope || [];
  },
  followingTasksLoading(state) {
    return state.followingTasksLoading;
  },
  registeredTasksLoading(state) {
    return state.registeredTasksLoading;
  },
};

const editTaskDebounced = debounce((commit, vm, userToken, newTask) => {
  editTask(userToken, newTask)
    .then(({ data }) => {
      const status = get(data, '[1].status');
      if (status === 'SUCCESS') {
        const projectId = get(data, '[1].data.project_id');
        commit(SET_PROJECT_ID, projectId);
        vm.dispatch('auth/setFlashMessage', { message: 'Effort saved successfully', type: 'success' });
        if (newTask.project_id === 'add_new_project') {
          vm.dispatch('projects/getProjectList');
        }
      } else {
        const message = get(data, '[1].error.error_usr_msg', 'Failed to save effort');
        vm.dispatch('auth/setFlashMessage', { message, type: 'error' });
      }
    })
    .catch(() => {
      vm.dispatch('auth/setFlashMessage', { message: 'Failed to save effort', type: 'error' });
    });
}, 1000);

const editLocationDebounced = debounce((commit, vm, userToken, newTask) => {
  editTask(userToken, newTask)
    .then(({ data }) => {
      const status = get(data, '[1].status');
      if (status === 'SUCCESS') {
        vm.dispatch('auth/setFlashMessage', { message: 'Effort saved successfully', type: 'success' });
      } else {
        const message = get(data, '[1].error.error_usr_msg', 'Failed to save effort');
        vm.dispatch('auth/setFlashMessage', { message, type: 'error' });
      }
    })
    .catch(() => {
      vm.dispatch('auth/setFlashMessage', { message: 'Failed to save effort', type: 'error' });
    });
}, 1000);

const editOrganizerDebounced = debounce((commit, vm, userToken, newTask) => {
  editTask(userToken, newTask)
    .then(({ data }) => {
      const status = get(data, '[1].status');
      if (status === 'SUCCESS') {
        vm.dispatch('auth/setFlashMessage', { message: 'Effort saved successfully', type: 'success' });
      } else {
        const message = get(data, '[1].error.error_usr_msg', 'Failed to save effort');
        vm.dispatch('auth/setFlashMessage', { message, type: 'error' });
      }
    })
    .catch(() => {
      vm.dispatch('auth/setFlashMessage', { message: 'Failed to save effort', type: 'error' });
    });
}, 1000);

const editTaskDatesDebounced = debounce((commit, vm, userToken, newTask) => {
  editTask(userToken, newTask)
    .then(({ data }) => {
      const status = get(data, '[1].status');
      if (status === 'SUCCESS') {
        vm.dispatch('auth/setFlashMessage', { message: 'Effort saved successfully', type: 'success' });
      } else {
        const message = get(data, '[1].error.error_usr_msg', 'Failed to save effort');
        vm.dispatch('auth/setFlashMessage', { message, type: 'error' });
      }
    })
    .catch(() => {
      vm.dispatch('auth/setFlashMessage', { message: 'Failed to save effort', type: 'error' });
    });
}, 1000);

const editSkillsDebounced = debounce((commit, vm, userToken, newTask) => {
  editTask(userToken, newTask)
    .then(({ data }) => {
      const status = get(data, '[1].status');
      if (status === 'SUCCESS') {
        vm.dispatch('auth/setFlashMessage', { message: 'Effort saved successfully', type: 'success' });
      } else {
        const message = get(data, '[1].error.error_usr_msg', 'Failed to save effort');
        vm.dispatch('auth/setFlashMessage', { message, type: 'error' });
      }
    })
    .catch(() => {
      vm.dispatch('auth/setFlashMessage', { message: 'Failed to save effort', type: 'error' });
    });
}, 1000);

const actions = {
  resetTasksFollowing({ commit }) {
    commit('RESET_TASKS_FOLLOWING');
  },
  resetTasksRegistered({ commit }) {
    commit('RESET_TASKS_REGISTERED');
  },
  resetTasksState({ commit }) {
    commit(RESET_TASK_STATE);
  },
  addMediaUrl({ commit }, task) {
    commit(ADD_MEDIA_URL, task.tasks_data);
    const dataUrl = get(task.tasks_data, '[0].data_url');
    const userToken = this.getters['auth/userToken'];
    addMediaUrl(userToken, dataUrl, task.task_id)
      .then(({ data }) => {
        const status = get(data, '[1].status');
        if (status === 'SUCCESS') {
          this.dispatch('auth/setFlashMessage', { message: 'Effort saved successfully', type: 'success' });
        } else {
          this.dispatch('auth/setFlashMessage', { message: 'Failed to save effort', type: 'error' });
        }
      })
      .catch(() => {
        this.dispatch('auth/setFlashMessage', { message: 'Failed to save effort', type: 'error' });
      });
  },
  hintToggle({ commit }, value) {
    saveState(value, 'showHint');
    commit(SHOW_HINT_TOGGLE, value);
  },
  addTaskDate({ commit }) {
    // this will add a new item to the task dates
    commit(NEW_TASK_DATES);
  },
  editLocations({ commit }, task) {
    const userToken = this.getters['auth/userToken'];
    commit(SET_TASK_LOCATION, task.tasks_locations);
    const vm = this;
    editLocationDebounced(commit, vm, userToken, task);
  },
  editDates({ commit }, task) {
    // taskDateObject contains task_id and task_date or task_duration
    // update the value in the store
    const userToken = this.getters['auth/userToken'];
    commit(SET_TASK_DATE, task);
    const vm = this;
    editTaskDatesDebounced(commit, vm, userToken, task);
  },
  reorderTaskMedia({ commit }, { taskMediaObjects, taskId }) {
    const taskDataList = [];
    const len = taskMediaObjects.length;
    for (let i = 0; i < taskMediaObjects.length; i += 1) {
      taskMediaObjects[i].task_data_rank = i + 1;
      const key = i + 1;
      const value = taskMediaObjects[i].data_object_id;
      taskDataList[i] = { [key]: value };
    }
    commit(REORDER_TASK_MEDIA, taskMediaObjects);
    const taskDataRankRange = {
      min_user_task_data_rank: 1,
      max_user_task_data_rank: len,
    };
    const userToken = this.getters['auth/userToken'];
    reorderTaskMedia(userToken, taskId, taskDataRankRange, taskDataList)
      .then(({ data }) => {
        const status = get(data, '[1].status');
        if (status === 'SUCCESS') {
          this.dispatch('auth/setFlashMessage', { message: 'Effort saved successfully', type: 'success' });
        } else {
          this.dispatch('auth/setFlashMessage', { message: 'Failed to save effort', type: 'error' });
        }
      })
      .catch(() => {
        this.dispatch('auth/setFlashMessage', { message: 'Failed to save effort', type: 'error' });
      });
  },
  reorderFollowingTasks({ commit }, tasks) {
    commit(REORDER_FOLLOWING_TASKS, tasks);
    const taskRankRange = { max_user_task_rank: tasks.length, min_user_task_rank: 1 };
    const userToken = this.getters['auth/userToken'];
    const userRegistered = 0;
    const taskList = [];
    for (let i = 0; i < tasks.length; i += 1) {
      taskList.push({ [i + 1]: tasks[i].task_id });
    }
    resortUserTaskList(userToken, taskRankRange, taskList, userRegistered)
      .then(({ data }) => {
        const status = get(data, '[1].status');
        if (status === 'SUCCESS') {
          this.dispatch('auth/setFlashMessage', { message: 'Effort saved successfully', type: 'success' });
        } else {
          this.dispatch('auth/setFlashMessage', { message: 'Failed to save effort', type: 'error' });
        }
      })
      .catch(() => {
        this.dispatch('auth/setFlashMessage', { message: 'Failed to save effort', type: 'error' });
      });
  },
  reorderRegisteredTasks({ commit }, tasks) {
    commit(REORDER_REGISTERED_TASKS, tasks);
    const taskRankRange = { max_user_task_rank: tasks.length, min_user_task_rank: 1 };
    const userToken = this.getters['auth/userToken'];
    const userRegistered = 1;
    const taskList = [];
    for (let i = 0; i < tasks.length; i += 1) {
      taskList.push({ [i + 1]: tasks[i].task_id });
    }
    resortUserTaskList(userToken, taskRankRange, taskList, userRegistered)
      .then(({ data }) => {
        const status = get(data, '[1].status');
        if (status === 'SUCCESS') {
          this.dispatch('auth/setFlashMessage', { message: 'Effort saved successfully', type: 'success' });
        } else {
          this.dispatch('auth/setFlashMessage', { message: 'Failed to save effort', type: 'error' });
        }
      })
      .catch(() => {
        this.dispatch('auth/setFlashMessage', { message: 'Failed to save effort', type: 'error' });
      });
  },
  followingTasks({ commit, state }) {
    if (state.followingTasksLoading || state.followingTasksExhausted) return;

    commit('FOLLOWING_TASKS_LOADING', true);

    const userToken = this.getters['auth/userToken'];
    const { followingTasksTake: take, followingTasksSkip: skip } = state;
    getUserTaskList(userToken, { skip, take, user_registered: 0 })
      .then(({ data }) => {
        const response = get(data, '[1]');
        const status = get(response, 'status');
        if (status === 'SUCCESS') {
          const tasks = get(response, 'data.details.task_list');
          const taskRankRange = get(response, 'data.details.task_rank_range');
          commit(FOLLOWING_TASKS, { tasks, taskRankRange });
        }

        if (get(data, '[1].error.error_type') === 'NO_DATA') {
          commit('FOLLOWING_TASKS_EXHAUSTED');
        }

        commit('FOLLOWING_TASKS_LOADING', false);
      });
  },
  registeredTasks({ commit }) {
    if (state.registeredTasksLoading || state.registeredTasksExhausted) return;

    commit('REGISTERED_TASKS_LOADING', true);

    const userToken = this.getters['auth/userToken'];
    const { registeredTasksTake: take, registeredTasksSkip: skip } = state;
    getUserTaskList(userToken, { take, skip, user_registered: 1 })
      .then(({ data }) => {
        this.dispatch('shared/setDataLoading', false);
        const response = get(data, '[1]');
        const status = get(response, 'status');

        if (status === 'SUCCESS') {
          const tasks = get(response, 'data.details.task_list');
          const taskRankRange = get(response, 'data.details.task_rank_range');
          commit(REGISTERED_TASKS, { tasks, taskRankRange });
        }

        if (get(data, '[1].error.error_type') === 'NO_DATA') {
          commit('REGISTERED_TASKS_EXHAUSTED');
        }

        commit('REGISTERED_TASKS_LOADING', false);
      });
  },
  editTask({ commit, state }, task) {
    const newTask = { ...state.task_data, ...task };
    commit(EDIT_TASK, newTask);
    const userToken = this.getters['auth/userToken'];
    // update the backend version with the same data
    const vm = this;

    const { project_id: projectId, task_name: name, task_description: description } = newTask;

    if (!projectId || !name || name.length < 20 || !description || description.lenght < 50) {
      return;
    }

    editTaskDebounced(commit, vm, userToken, newTask);
  },
  editSkills({ commit }, taskSkills) {
    commit(EDIT_TASK_SKILLS, taskSkills);
    const userToken = this.getters['auth/userToken'];
    // extract only the task id and add rank for network request
    taskSkills.tasks_skills = taskSkills.tasks_skills.map((skill, index) => ({
      skill_id: skill.skill_id,
      task_skill_rank: index + 1,
    }));
    const vm = this;
    editSkillsDebounced(commit, vm, userToken, taskSkills);
  },
  editOrganizers({ commit }, taskOrganizers) {
    const userToken = this.getters['auth/userToken'];
    const vm = this;
    editOrganizerDebounced(commit, vm, userToken, taskOrganizers);
  },
  getTaskEditDetails({ commit }, taskId) {
    commit('TASK_EDIT_LOADING', true);
    const userToken = this.getters['auth/userToken'];
    return getTaskEditDetails(userToken, taskId)
      .then(({ data }) => {
        commit('TASK_EDIT_LOADING', false);
        const response = get(data, '[1]');
        const status = get(data, '[1].status');
        if (status === 'SUCCESS') {
          const task = get(data, '[1].data.details');
          commit(GET_TASK_EDIT_DETAILS, task);
        } else {
          const error = get(response, 'error');
          commit(SET_ERROR, error);
        }
      });
  },
  getTaskDetails({ commit }, taskId) {
    this.dispatch('shared/setDataLoading', true);
    const userToken = this.getters['auth/userToken'];
    return getTaskDetails(userToken, taskId)
      .then(({ data }) => {
        this.dispatch('shared/setDataLoading', false);
        const response = get(data, '[1]');
        const status = get(data, '[1].status');
        if (status === 'SUCCESS') {
          const taskDetails = get(data, '[1].data');
          const task = get(taskDetails, 'details[0]');
          const allowedChannelAccess = get(taskDetails, 'details[0].task_data.channel_types_access', []);
          commit(GET_TASK_EDIT_DETAILS, task);
          commit(SET_ALLOWED_CHANNEL_ACCESS, allowedChannelAccess);
        } else {
          const error = get(response, 'error');
          commit(SET_ERROR, error);
        }
      })
      .catch(() => {
        this.dispatch('shared/setDataLoading', false);
      });
  },
  unfollowTask({ commit }, taskId) {
    const userToken = this.getters['auth/userToken'];
    removeTaskFromUser(userToken, taskId, 0)
      .then(({ data }) => {
        const status = get(data, '[1].status');
        if (status === 'SUCCESS') {
          commit(UNFOLLOW_TASK);
          this.dispatch('auth/setFlashMessage', { message: 'Unfollow effort successful', type: 'success' });
        } else {
          this.dispatch('auth/setFlashMessage', { message: 'Failed to unfollow the effort', type: 'error' });
        }
      })
      .catch(() => {
        this.dispatch('auth/setFlashMessage', { message: 'Failed to unregister the effort', type: 'error' });
      });
  },
  unregisterTask({ commit }, taskId) {
    const userToken = this.getters['auth/userToken'];
    removeTaskFromUser(userToken, taskId, 1)
      .then(({ data }) => {
        const status = get(data, '[1].status');
        if (status === 'SUCCESS') {
          commit(UNFOLLOW_TASK);
          this.dispatch('auth/setFlashMessage', { message: 'You are now unregistered from the effort', type: 'success' });
        } else {
          this.dispatch('auth/setFlashMessage', { message: 'Failed to unregister the effort', type: 'error' });
        }
      })
      .catch(() => {
        this.dispatch('auth/setFlashMessage', { message: 'Failed to unregister the effort', type: 'error' });
      });
  },
  followTask({ commit }, taskId) {
    const userToken = this.getters['auth/userToken'];
    addTaskToUser(userToken, taskId, 0)
      .then(({ data }) => {
        const status = get(data, '[1].status');
        if (status === 'SUCCESS') {
          commit(UPDATE_TASK_SCOPE, ['follower']);
          this.dispatch('auth/setFlashMessage', { message: 'You are now following this effort', type: 'success' });
        } else {
          this.dispatch('auth/setFlashMessage', { message: 'Failed to follow the effort', type: 'error' });
        }
      })
      .catch(() => {
        this.dispatch('auth/setFlashMessage', { message: 'Failed to follow the effort', type: 'error' });
      });
  },
  registerTask({ commit }, taskId) {
    const userToken = this.getters['auth/userToken'];
    addTaskToUser(userToken, taskId, 1)
      .then(({ data }) => {
        const status = get(data, '[1].status');
        if (status === 'SUCCESS') {
          commit(UPDATE_TASK_SCOPE, ['registered']);
          this.dispatch('auth/setFlashMessage', { message: 'You are now registered for this effort', type: 'success' });
        } else {
          this.dispatch('auth/setFlashMessage', { message: 'Failed to register for the effort', type: 'error' });
        }
      })
      .catch(() => {
        this.dispatch('auth/setFlashMessage', { message: 'Failed to register for the effort', type: 'error' });
      });
  },
  getTaskEditDefaults({ commit }) {
    const userToken = this.getters['auth/userToken'];

    getTaskEditDefaults(userToken)
      .then(({ data }) => {
        const defaults = get(data, '[1].data.details');
        // console.log('task defaults', defaults);
        commit(TASK_EDIT_DEFAULTS, defaults);
      });
  },
  tasksObjectAdd({ commit }, taskObject) {
    commit(TASKS_OBJECT_ADD, taskObject);
  },
  tasksObjectRemove({ commit }, { index, taskId }) {
    const userToken = this.getters['auth/userToken'];
    const taskObject = this.getters['tasks/taskObjects'][index] || {};
    const taskObjectId = taskObject.data_object_id;
    removeMedia(userToken, { data_object_id: taskObjectId, task_id: taskId })
      .then(({ data }) => {
        const status = get(data, '[1].status');
        commit(TASKS_OBJECT_REMOVE, index);
        if (status === 'SUCCESS') {
          this.dispatch('auth/setFlashMessage', { message: 'Effort saved successfully', type: 'success' });
        } else {
          this.dispatch('auth/setFlashMessage', { message: 'Failed to save effort', type: 'error' });
        }
      })
      .catch(() => {
        this.dispatch('auth/setFlashMessage', { message: 'Failed to save effort', type: 'error' });
      });
  },
};

const mutations = {
  [SHOW_HINT_TOGGLE](state, value) {
    state.showHint = value;
  },
  [SET_TASK_LOCATION](state, tasksLocations) {
    state.tasks_locations = tasksLocations;
  },
  [SET_PROJECT_ID](state, projectId) {
    state.task_data.project_id = projectId;
  },
  [SET_ALLOWED_CHANNEL_ACCESS](state, allowedChannelAccess) {
    state.allowed_channel_access = allowedChannelAccess;
  },
  [RESET_TASK_STATE](state) {
    const s = defaultState();
    Object.keys(s).forEach(key => state[key] = s[key]);
  },
  [REORDER_FOLLOWING_TASKS](state, tasks) {
    state.followingTasks = tasks;
  },
  [REORDER_REGISTERED_TASKS](state, tasks) {
    state.registeredTasks = tasks;
  },
  [REORDER_TASK_MEDIA](state, taskMediaObject) {
    state.tasks_data = taskMediaObject;
  },
  [ADD_MEDIA_URL](state, tasksData) {
    state.tasks_data = [...state.tasks_data, ...tasksData];
  },
  [BROWSE_TASKS](state, values) {
    state.tasks = values.tasks || [];
    state.error = values.errors || {};
  },
  [SET_ERROR](state, error) {
    state.error = error || {};
  },
  [FOLLOWING_TASKS](state, values) {
    state.followingTasks = values.tasks || [];
    state.followingTasksRankRange = values.taskRankRange || {};
    state.followingTasksSkip += state.followingTasksTake;
  },
  FOLLOWING_TASKS_LOADING(state, value) {
    state.followingTasksLoading = value;
  },
  FOLLOWING_TASKS_EXHAUSTED(state) {
    state.followingTasksExhausted = true;
  },
  [REGISTERED_TASKS](state, values) {
    state.registeredTasks = values.tasks || [];
    state.registeredTasksRankRange = values.taskRankRange || {};
    state.registeredTasksSkip += state.registeredTasksTake;
  },
  REGISTERED_TASKS_LOADING(state, value) {
    state.registeredTasksLoading = value;
  },
  REGISTERED_TASKS_EXHAUSTED(state) {
    state.registeredTasksExhausted = true;
  },
  [EDIT_TASK](state, task) {
    state.task_data = {
      ...state.task_data,
      ...task,
    };
  },
  TASK_EDIT_LOADING(state, value) {
    state.taskEditLoading = value;
  },
  [EDIT_TASK_SKILLS](state, values) {
    state.task_skills = values.task_skills;
  },
  [GET_TASK_EDIT_DETAILS](state, task) {
    const oldTaskData = get(state, 'task_data', {});
    const newTaskData = get(task, 'task_data', {});
    // handle task status
    newTaskData.task_status = newTaskData.task_status || '0';

    state.task_data = {
      ...oldTaskData,
      ...newTaskData,
    };

    const tasksData = get(task, 'tasks_data', []);
    tasksData.sort((a, b) => a.task_data_rank - b.task_data_rank);
    state.tasks_data = tasksData;

    const tasksDates = get(task, 'tasks_dates', []);
    state.tasks_dates = tasksDates;

    const tasksLocations = get(task, 'tasks_locations', []);
    state.tasks_locations = tasksLocations;

    const tasksSkills = get(task, 'tasks_skills', []);
    state.tasks_skills = tasksSkills;

    const tasksTags = get(task, 'tasks_tags', []);
    state.tasks_tags = tasksTags;

    const tasksOrganizers = get(task, 'tasks_organizers', []);
    state.tasks_organizers = tasksOrganizers;
  },
  [TASK_EDIT_DEFAULTS](state, defaults) {
    state.task_edit_defaults = defaults;
  },
  [TASKS_OBJECT_ADD](state, taskObject) {
    state.tasks_data = [
      ...state.tasks_data,
      taskObject,
    ];
  },
  [SET_TASK_DATE](state, task) {
    state.tasks_dates = task.tasks_dates;
  },
  [NEW_TASK_DATES](state) {
    // NOTE: this condition is added as we currently just want to use one location
    if (state.tasks_dates && state.tasks_dates.length > 0) {
      return;
    }
    state.tasks_dates = [{ task_duration: null, task_date: null }];
  },
  [TASKS_OBJECT_REMOVE](state, index) {
    state.tasks_data = [
      ...state.tasks_data.slice(0, index),
      ...state.tasks_data.slice(index + 1, state.tasks_data.length),
    ];
  },
  [UPDATE_TASK_SCOPE](state, scopes) {
    state.task_data.user_task_scope = state.task_data.user_task_scope || [];
    state.task_data.user_task_scope = [...state.task_data.user_task_scope, ...scopes];
  },
  [UNFOLLOW_TASK](state) {
    state.task_data.user_task_scope = [];
  },
  RESET_TASKS_FOLLOWING(state) {
    state.followingTasksLoading = false;
    state.followingTasks = [];
    state.followingTasksRankRange = {};
    state.followingTasksTake = 20;
    state.followingTasksSkip = 0;
    state.followingTasksExhausted = false;
  },
  RESET_TASKS_REGISTERED(state) {
    state.registeredTasksLoading = false;
    state.registeredTasks = [];
    state.registeredTasksRankRange = {};
    state.registeredTasksTake = 20;
    state.registeredTasksSkip = 0;
    state.registeredTasksExhausted = false;
  },
};


export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/tasks.js